import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { EditButton, ListButton, ReferenceField, RichTextField, Show, Tab, TabbedShowLayout, TextField, TopToolbar, useRecordContext } from 'react-admin';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, Link, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField as MuiTextField, Tooltip, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import { __t } from '../../i18n/t';
import { DevRequest, DevRequestAction as A, DevRequestProject, DevRequestStatus as S } from '../../models/devRequest';
import { fmtDateTime, fmtDuration } from '../attendantGoalOffers/labels';
import { allowedActions, canEdit, DEV_ACTIONS, isDraft } from './actions';
import InternalGate from './InternalGate';
import {
  deployStatusColor, deployStatusLabel, eventLabel, fmtUsd, requestStatus, resumeCommand, typeLabel,
} from './labels';
import { ConfirmActionButton, CopyBlock, Muted, Prose, RegisterPasskeyButton, Scroll, Section, TextActionButton, useApprover, useDevAction } from './parts';
import MarkdownRenderer from '../../manual/components/MarkdownRenderer';

const Title = () => {
  const r = useRecordContext<DevRequest>();
  return <span>{r ? `${__t('Pedido')} #${r.number} · ${r.title}` : ''}</span>;
};

const ShowActions = () => {
  const r = useRecordContext<DevRequest>();
  return (
    <TopToolbar>
      <ListButton />
      {r && canEdit(r.status, r.resumeReason) && <EditButton />}
    </TopToolbar>
  );
};

const Link2 = ({ href, children }: { href?: string | null; children: string }) =>
  (href ? <Link href={href} target="_blank" rel="noopener noreferrer">{children}</Link> : <span>—</span>);

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <TableRow>
    <TableCell sx={{ width: 200, fontWeight: 500, verticalAlign: 'top' }}>{label}</TableCell>
    <TableCell>{children}</TableCell>
  </TableRow>
);

// ---------- Aba Pedido (suporte) ----------

const ANSWER_HINT = () => __t('Responda todas as perguntas, na mesma ordem, uma por linha. Se não souber alguma, diga que não sabe: o robô decide sozinho.');

const AnswerBox = ({ run, busy, allowed }: { run: ReturnType<typeof useDevAction>['run']; busy: boolean; allowed: boolean }) => {
  const [text, setText] = useState('');
  return (
    <Box sx={{ mt: 2, maxWidth: 640 }}>
      <Tooltip title={ANSWER_HINT()} placement="top-start" arrow>
        <MuiTextField
          multiline
          minRows={4}
          fullWidth
          label={__t('Respostas')}
          helperText={ANSWER_HINT()}
          value={text}
          onChange={e => setText(e.target.value)}
        />
      </Tooltip>
      <Button sx={{ mt: 1 }} variant="contained" disabled={busy || !allowed || !text.trim()} onClick={() => run(A.answer, { text: text.trim() })}>
        {__t('Enviar respostas')}
      </Button>
    </Box>
  );
};

/** Tudo que o robô vai receber: o texto e as imagens que o backend assina e entrega ao worker. */
const RobotPreview = ({ r }: { r: DevRequest }) => (
  <>
    <Typography variant="body2" sx={{ mb: 1 }}>{__t('O robô vai ler exatamente isto. Se tiver algo que você não escreveu, não envie.')}</Typography>
    <Box sx={{ maxHeight: 360, overflow: 'auto', p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}><Prose text={r.requestMd} /></Box>
    {!!r.imageSrcs?.length && (
      <Stack direction="row" sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
        {r.imageSrcs.map((src, i) => {
          const label = __t('Imagem %{n}', { n: i + 1 });
          return (
            <Box key={`${i}-${src.slice(0, 80)}`}>
              <Typography variant="caption">{label}</Typography>
              <Box component="img" src={src} alt={label} sx={{ display: 'block', maxWidth: 200, maxHeight: 160 }} />
            </Box>
          );
        })}
      </Stack>
    )}
  </>
);

const SupportTab = () => {
  const r = useRecordContext<DevRequest>();
  const { run, busy } = useDevAction();
  const me = useApprover();
  const location = useLocation();
  const navigate = useNavigate();
  if (!r) { return null; }
  const allowed = allowedActions(r.status);
  const done = [S.delivered, S.resolved].includes(r.status);
  const draft = isDraft(r);
  const status = requestStatus(r);
  const talksToRobot = draft || allowed.includes(A.answer) || allowed.includes(A.reopen);
  // Vindo de "Salvar": a conferência já abre, e o envio fica num passo só (salvar e digitar o PIN).
  const sendNow = !!(location.state as { sendNow?: boolean } | null)?.sendNow && draft && !!me?.isSender;
  const sendButton = draft && me?.isSender ? (
    <ConfirmActionButton
      buttonLabel={__t('Enviar ao robô')}
      title={__t('Enviar ao robô?')}
      content={<RobotPreview r={r} />}
      variant="contained"
      busy={busy}
      autoOpen={sendNow}
      onAutoOpen={() => navigate(location.pathname, { replace: true, state: null })}
      onConfirm={() => run(A.send, { diffSha: r.contentSha })}
    />
  ) : null;
  return (
    <Box>
      <Section title={__t('Situação')}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
          <Chip color={status.color} label={status.label} />
          {r.urgent && <Chip color="error" variant="outlined" size="small" label={__t('Urgente')} />}
        </Stack>
        {draft ? (
          <Alert severity="warning" sx={{ mt: 1, alignItems: 'center' }} action={sendButton}>{status.hint}</Alert>
        ) : (
          <Typography variant="body2">{status.hint}</Typography>
        )}

        {talksToRobot && me && !me.isSender && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {__t('Para falar com o robô, esta conta precisa de digital ou PIN e da liberação do desenvolvedor.')}
            {!me.hasPasskey && <Box sx={{ mt: 1 }}><RegisterPasskeyButton /></Box>}
          </Alert>
        )}

        {r.status === S.waitingSupport && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>{__t('Perguntas do robô')}</Typography>
            <Prose text={r.questions} />
            <AnswerBox run={run} busy={busy} allowed={!!me?.isSender} />
          </>
        )}
        {r.status !== S.waitingSupport && r.questions && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>{__t('Perguntas do robô')}</Typography>
            <Prose text={r.questions} />
            {r.answers && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>{__t('Respostas')}</Typography>
                <Prose text={r.answers} />
              </>
            )}
          </>
        )}

        {done && (
          <Box sx={{ mt: 2 }}>
            {r.summary && (
              <>
                <Typography variant="subtitle2">{__t('O que foi feito')}</Typography>
                <Prose text={r.summary} />
              </>
            )}
            {r.rootCause && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>{__t('Causa')}</Typography>
                <Prose text={r.rootCause} />
              </>
            )}
          </Box>
        )}
        {(done || r.requiresAppUpdate) && (
          <Alert
            sx={{ mt: 2 }}
            severity={r.requiresAppUpdate ? 'warning' : 'success'}
            icon={r.requiresAppUpdate ? <PhoneIphoneIcon fontSize="inherit" /> : undefined}
          >
            {r.requiresAppUpdate ? __t('Precisa atualizar o app: sim.') : __t('Precisa atualizar o app: não.')}
            {r.requiresAppUpdate && r.appUpdateNotes ? ` ${r.appUpdateNotes}` : ''}
          </Alert>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
          {allowed.includes(A.resolve) && (
            <ConfirmActionButton
              buttonLabel={__t('Resolveu')}
              title={__t('Marcar como resolvido?')}
              content={__t('Use quando você conferiu e está tudo certo.')}
              color="success"
              variant="contained"
              busy={busy}
              onConfirm={() => run(A.resolve)}
            />
          )}
          {allowed.includes(A.reopen) && me?.isSender && (
            <TextActionButton
              action={A.reopen}
              buttonLabel={__t('Reabrir')}
              title={__t('Reabrir pedido')}
              label={__t('O que ainda não está certo?')}
              required
              confirmLabel={__t('Reabrir')}
              run={run}
              busy={busy}
            />
          )}
          {allowed.includes(A.cancel) && (
            <ConfirmActionButton
              buttonLabel={__t('Cancelar pedido')}
              title={__t('Cancelar este pedido?')}
              content={__t('O robô para de trabalhar nele.')}
              color="error"
              busy={busy}
              onConfirm={() => run(A.cancel)}
            />
          )}
          <TextActionButton
            action={A.comment}
            buttonLabel={__t('Comentar')}
            title={__t('Comentário')}
            label={__t('Comentário')}
            required
            confirmLabel={__t('Enviar')}
            run={run}
            busy={busy}
          />
        </Stack>
      </Section>

      <Section title={__t('Pedido')}>
        <Scroll>
          <Table size="small">
            <TableBody>
              <Field label={__t('Tipo')}>{typeLabel(r.type)}</Field>
              <Field label={__t('Quem pediu')}>{r.requesterName || '—'}</Field>
              <Field label={__t('Rede')}>
                {r.chainId ? (
                  <ReferenceField record={{ id: r.chainId, ref: r.chainId }} source="ref" reference="chains" link="show" emptyText={r.chainId.slice(0, 8)}>
                    <TextField source="name" />
                  </ReferenceField>
                ) : '—'}
              </Field>
              <Field label={__t('URL de referência')}><Link2 href={r.referenceUrl}>{r.referenceUrl ?? ''}</Link2></Field>
              <Field label={__t('Criado em')}>{fmtDateTime(r.createdAt)}</Field>
            </TableBody>
          </Table>
        </Scroll>
        <Box sx={{ mt: 2, '& img': { maxWidth: '100%' } }}>
          <RichTextField source="body" label={false} />
        </Box>
      </Section>
    </Box>
  );
};

// ---------- Aba Dev ----------

const DevActions = () => {
  const r = useRecordContext<DevRequest>();
  const { run, busy } = useDevAction();
  const me = useApprover();
  if (!r) { return null; }
  const allowed = allowedActions(r.status);
  const pending = allowed.some(a => DEV_ACTIONS.includes(a));
  const can = (a: A) => allowed.includes(a) && !!me?.isApprover;
  return (
    <Section title={__t('Ações do dev')}>
      {pending && me && !me.isApprover && <Alert severity="info" sx={{ mb: 2 }}>{__t('Só o desenvolvedor libera esta etapa.')}</Alert>}
      {me && !me.hasPasskey && <Box sx={{ mb: 2 }}><RegisterPasskeyButton /></Box>}
      {r.status === S.waitingMigration && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>{__t('Rode este SQL no banco de produção e confirme.')}</Typography>
          {r.migrationSql ? <CopyBlock text={r.migrationSql} /> : <Muted text={__t('Sem SQL de arquivos novos: confira o diff.')} />}
          {r.migrationDiff && (
            <Accordion variant="outlined" sx={{ mt: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>{__t('Diff do schema (master..develop)')}</AccordionSummary>
              <AccordionDetails><CopyBlock text={r.migrationDiff} /></AccordionDetails>
            </Accordion>
          )}
          {r.migrationDiffUrl && <Box sx={{ mt: 1 }}><Link2 href={r.migrationDiffUrl}>{__t('Ver no GitHub')}</Link2></Box>}
        </Box>
      )}
      {r.status === S.waitingApproval && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <b>{__t('Motivo do robô')}:</b> <span style={{ whiteSpace: 'pre-wrap' }}>{r.approvalReason}</span>
        </Alert>
      )}
      {r.status === S.failed && r.lastError && <Alert severity="error" sx={{ mb: 2 }}>{r.lastError}</Alert>}
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        {can(A.migrated) && (
          <ConfirmActionButton
            buttonLabel={__t('Já migrei')}
            title={__t('Confirmar migração?')}
            content={__t('O pedido volta para a fila e o deploy segue.')}
            variant="contained"
            busy={busy}
            onConfirm={() => run(A.migrated, { diffSha: r.migrationDiffSha ?? undefined })}
          />
        )}
        {can(A.approve) && (
          <TextActionButton
            action={A.approve}
            buttonLabel={__t('Aprovar')}
            title={__t('Aprovar pedido')}
            label={__t('Observação (opcional)')}
            required={false}
            confirmLabel={__t('Aprovar')}
            variant="contained"
            run={run}
            busy={busy}
          />
        )}
        {can(A.reject) && (
          <TextActionButton
            action={A.reject}
            buttonLabel={__t('Recusar')}
            title={__t('Recusar pedido')}
            label={__t('Motivo (opcional)')}
            required={false}
            confirmLabel={__t('Recusar')}
            color="error"
            run={run}
            busy={busy}
          />
        )}
        {can(A.retry) && (
          <Button size="small" variant="contained" disabled={busy} onClick={() => run(A.retry)}>{__t('Tentar de novo')}</Button>
        )}
      </Stack>
      {!pending && <Muted text={__t('Nenhuma ação do dev pendente.')} />}
    </Section>
  );
};

const runLabel = (run: NonNullable<DevRequestProject['runs']>[number]) => `${run.workflowName}: ${run.conclusion ?? run.status}`;

const ProjectsSection = () => {
  const r = useRecordContext<DevRequest>();
  const projects = r?.projects ?? [];
  return (
    <Section title={__t('Projetos')}>
      {projects.length === 0 ? <Muted text={__t('Nenhum projeto ainda.')} /> : (
        <Scroll>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{__t('Repositório')}</TableCell>
                <TableCell>{__t('Branch')}</TableCell>
                <TableCell>{__t('Commits')}</TableCell>
                <TableCell>{__t('PR develop')}</TableCell>
                <TableCell>{__t('PR deploy')}</TableCell>
                <TableCell>{__t('Deploy')}</TableCell>
                <TableCell>{__t('Runs')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.map(p => (
                <TableRow key={p.repo}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.repo}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{p.branch}</TableCell>
                  <TableCell>
                    <Stack spacing={0.25}>
                      {(p.commits ?? []).map(c => (
                        <Link key={c.sha} href={c.url} target="_blank" rel="noopener noreferrer" title={c.message} sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {c.sha.slice(0, 7)}
                        </Link>
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell><Link2 href={p.developPrUrl}>PR</Link2></TableCell>
                  <TableCell><Link2 href={p.deployPrUrl}>PR</Link2></TableCell>
                  <TableCell>
                    <Chip size="small" color={deployStatusColor(p.deployStatus)} label={deployStatusLabel(p.deployStatus)} />
                    {p.deployAttempts > 1 && <Typography variant="caption" sx={{ ml: 0.5 }}>{__t('%{n} tentativas', { n: p.deployAttempts })}</Typography>}
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.25}>
                      {(p.runs ?? []).map(run => <Link key={run.id} href={run.url} target="_blank" rel="noopener noreferrer">{runLabel(run)}</Link>)}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Scroll>
      )}
    </Section>
  );
};

const TotalsSection = () => {
  const r = useRecordContext<DevRequest>();
  const copy = r?.sessionId ? resumeCommand(r.sessionId) : null;
  if (!r) { return null; }
  return (
    <Section title={__t('Execução')}>
      <Table size="small" sx={{ maxWidth: 520 }}>
        <TableBody>
          <Field label={__t('Custo')}>{fmtUsd(r.totals?.costUsd)}</Field>
          <Field label={__t('Tempo de Claude')}>{fmtDuration(r.totals?.claudeMs === undefined ? null : r.totals.claudeMs / 1000)}</Field>
          <Field label={__t('Execuções do Claude')}>{r.totals?.runs ?? '—'}</Field>
          <Field label={__t('Tentativas')}>{r.attemptCount}</Field>
          <Field label={__t('Falhas')}>{r.failureCount}</Field>
        </TableBody>
      </Table>
      {copy && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{__t('Retomar a sessão no terminal')}</Typography>
          <CopyBlock text={copy} />
        </Box>
      )}
    </Section>
  );
};

const NotesSection = () => {
  const r = useRecordContext<DevRequest>();
  if (!r || !(r.devNotes || r.assumptions || r.lastError)) { return null; }
  return (
    <Section title={__t('Notas do dev')}>
      {r.devNotes && <MarkdownRenderer content={r.devNotes} />}
      {r.assumptions && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 1.5 }}>{__t('Suposições do robô')}</Typography>
          <MarkdownRenderer content={r.assumptions} />
        </>
      )}
      {r.lastError && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 1.5 }}>{__t('Último erro')}</Typography>
          <Prose text={r.lastError} />
        </>
      )}
    </Section>
  );
};

const TimelineSection = () => {
  const r = useRecordContext<DevRequest>();
  const events = r?.events ?? [];
  return (
    <Section title={__t('Linha do tempo')}>
      {events.length === 0 ? <Muted text={__t('Sem eventos.')} /> : (
        <Scroll>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{__t('Horário')}</TableCell>
                <TableCell>{__t('Evento')}</TableCell>
                <TableCell>{__t('Quem')}</TableCell>
                <TableCell>{__t('Mensagem')}</TableCell>
                <TableCell>{__t('Duração')}</TableCell>
                <TableCell>{__t('Custo')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map(e => (
                <TableRow key={e.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDateTime(e.createdAt)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{eventLabel(e.type)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{e.actor}</TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-wrap', minWidth: 280 }}>{e.message}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{e.durationMs == null ? '—' : fmtDuration(e.durationMs / 1000)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{e.costUsd == null ? '—' : fmtUsd(e.costUsd)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Scroll>
      )}
    </Section>
  );
};

const DevTab = () => (
  <Box>
    <DevActions />
    <ProjectsSection />
    <TotalsSection />
    <NotesSection />
    <TimelineSection />
  </Box>
);

const DevRequestShow = () => (
  <InternalGate>
    <Show queryOptions={{ refetchInterval: 30000 }} title={<Title />} actions={<ShowActions />}>
      <TabbedShowLayout>
        <Tab label="Pedido"><SupportTab /></Tab>
        <Tab label="Dev" path="dev"><DevTab /></Tab>
      </TabbedShowLayout>
    </Show>
  </InternalGate>
);

export default DevRequestShow;
